import { animate, state, style, transition, trigger } from '@angular/animations';
import { SelectionModel } from '@angular/cdk/collections';
import { NestedTreeControl } from '@angular/cdk/tree';
import { Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatTreeNestedDataSource } from '@angular/material/tree';
import { ActivatedRoute } from '@angular/router';
import { combineLatest, of, Subject, timer } from 'rxjs';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { catchError, filter, map, mergeMap, tap } from 'rxjs/operators';
import { v4 as generateUuid } from 'uuid';
import { AwsOnboardingService } from '../../../onboarding/service/aws-onboarding/aws-onboarding.service';
import { ConfigFacadeService } from '@core/services/config/config-facade.service';
import { IProcessStatus } from '@app/interfaces/process.interface';
import { PermissionNode } from '../../../onboarding/interfaces/onboarding.interface';
import { OnboardingCloudEnum } from './onboarding-cloud-type.enum';

export interface IPermissionsTreeOutput {
	permissionsAndFeatures: {
		permissions: any;
		featuresList: string[];
	};
	permissionsLevel: string;
}

const NODES_NAMES_ALLOW_PARTIAL_TREE = ['Dynamize Kubernetes'];

@Component({
	selector: 'app-onboarding-permissions-tree',
	templateUrl: './onboarding-permissions-tree.component.html',
	styleUrls: ['./onboarding-permissions-tree.component.scss'],
	encapsulation: ViewEncapsulation.None,
	animations: [
		trigger('slideVertical', [
			state('hide', style({ height: 0 })),
			state('show', style({ height: '*' })),
			transition('* => *', [animate('{{ delay }}ms cubic-bezier(0.25, 0.8, 0.25, 1)')], {
				params: { delay: 0 },
			}),
		]),
	],
})
export class OnboardingPermissionsTreeComponent implements OnInit {
	readonly CUR_V2_PERMISSION_KEY = 'BCM Data Exports (Read-Only)';

	// when cur v2 is selected it should be enabled when cur v1 or no CUR it should be unselected
	@Input() set updatePermission(options: { update: boolean; value: boolean } | null) {
		if (!options?.update) return;
		const node = this.dataSource.data.find((node) => node.name === this.CUR_V2_PERMISSION_KEY);
		if (!node) return;
		const isSelected = this.descendantsAllSelected(node);
		if (isSelected === options.value) return;
		this.permissionSelectionToggle(node);
	}
	@Input() submitSubject: Subject<boolean>;
	@Input() partnersMode = false;
	@Input() partnersAccountId = '';
	@Input() readonly cloud: OnboardingCloudEnum = OnboardingCloudEnum.Aws;

	@Output() emitPermissions = new EventEmitter<IPermissionsTreeOutput>();
	accountId: string;

	delayTime = 0;

	permissionsData: any;
	accountPermissions: any;
	public isLoading = false;
	processStatus: IProcessStatus = IProcessStatus.loading;
	@Output() disableSubmit = new EventEmitter();

	defaultTemplate: PermissionNode[] = [];
	managedTemplate: PermissionNode[] = [];

	readonly PRODUCT_LABEL: string;
	readonly cloudTypes = OnboardingCloudEnum;
	public readonly permissionTemplates = new FormGroup({
		control: new FormControl('readonly'),
	});

	dataChange$ = new BehaviorSubject<PermissionNode[]>([]);

	get data(): PermissionNode[] {
		return this.dataChange$.value;
	}

	treeControl: NestedTreeControl<PermissionNode>;
	dataSource: MatTreeNestedDataSource<PermissionNode>;
	/** The selection for checklist */
	checklistSelection = new SelectionModel<PermissionNode>(true);

	TREE_DATA = {};
	tree: any;

	permissionsToUpdate: string[] = [];

	mandatoryPermissions: string[] = [];

	private _nodesNamesAllowingPartialTree: string[] = NODES_NAMES_ALLOW_PARTIAL_TREE;
	private _initiallySelectedReadOnlyNodes: PermissionNode[] = [];
	private _initiallySelectedManagedNodes: PermissionNode[] = [];

	constructor(
		private onboardingService: AwsOnboardingService,
		private route: ActivatedRoute,
		private appConfig: ConfigFacadeService,
	) {
		this.PRODUCT_LABEL = this.appConfig.config.partner.prefix;
		this.treeControl = new NestedTreeControl<PermissionNode>(this.getChildren);
		this.dataSource = new MatTreeNestedDataSource();

		this.dataChange$.subscribe((data) => {
			this.dataSource.data = data;
			this.treeControl.dataNodes = data;
		});

		this.checklistSelection.changed.subscribe(
			(event) => (this.onboardingService.selectedPermissions = this.checklistSelection.selected),
		);
	}

	ngOnInit(): void {
		this.disableSubmit.emit(false);

		this.route.params.subscribe((params) => {
			this.permissionsToUpdate = params.permissions || null;
			const id = params.accountId || this.partnersAccountId;
			if (id) {
				this.accountId = id;
				this.onboardingService.accountId = id;
			} else {
				this.onboardingService.isReonboarding = false;
				this.onboardingService.currentUUID = generateUuid();
			}
		});
		this.isLoading = true;
		this.permissionTemplates.controls.control.disable();

		this.submitSubject
			.pipe(
				filter((value) => value),
				tap(() => {
					this.emitPermissions.emit({
						permissionsAndFeatures: this.collectPermissions(),
						permissionsLevel: this.permissionTemplates.controls.control.value,
					});
				}),
			)
			.subscribe();

		this.handleTemplatesChange();

		this.startTreeInit();
	}

	private initManagedAndDefaultTrees(): void {
		this.selectNodesByTemplate(this.treeControl.dataNodes, this.defaultTemplate, true);
		this._initiallySelectedReadOnlyNodes = structuredClone(
			this.findSelectedNodes(this.treeControl, this.checklistSelection, this.descendantsPartiallySelected.bind(this)),
		);
		this.selectNodesByTemplate(this.treeControl.dataNodes, this.managedTemplate);
		this._initiallySelectedManagedNodes = structuredClone(
			this.findSelectedNodes(this.treeControl, this.checklistSelection, this.descendantsPartiallySelected.bind(this)),
		);
	}

	private startTreeInit() {
		this.onboardingService.getPermissionsMap(this.PRODUCT_LABEL, this.cloud).subscribe((resp) => {
			this.permissionsData = resp.permissions_map;
			this.onboardingService.jsonTemplate = resp.template;
			this.castDataToTreeObject();
			this.onboardingService.permissionsTree = this.TREE_DATA;
			// Build the tree nodes-list from Json object. The result is a list of `PermissionNode` with nested
			//     nodes-list as children.
			const data = this.buildPermissionsTree(this.TREE_DATA, 0, null);
			this.initManagedAndDefaultTrees();
			this.dataChange$.next(data);
			if (this.accountId) {
				this.getAccountPermissions();
			} else {
				this.permissionTemplates.patchValue({ control: 'readonly' });
			}
			this.disableMandatoryPermissions();
			this.expandServiceLevel();
			// when there is account id we`ll add animation and disable loading after account permissions request finishes
			if (!this.accountId) {
				this.treeLoadingWorkAround();
			}
		});
	}

	private getAccountPermissions() {
		this.onboardingService
			.getAccountTK(this.accountId)
			.pipe(
				map((resp) => resp.tk),
				tap((uuid) => (this.onboardingService.currentUUID = uuid)),
				tap(() => (this.onboardingService.isReonboarding = true)),
				mergeMap((uuid) => {
					const source = [
						this.onboardingService.getAccountPermissions(uuid),
						this.onboardingService.getAccountFeaturesList(uuid),
					];
					return combineLatest(source);
				}),
				catchError((err) => {
					console.warn('Error: ', err);
					return of([]);
				}),
				// @ts-ignore
			)
			.subscribe((permissionsAndFeatures) => {
				if (!permissionsAndFeatures.length) {
					this.processStatus = IProcessStatus.failed;
					this.disableSubmit.emit(true);
					this.treeLoadingWorkAround();
					return;
				}
				const permissions = permissionsAndFeatures[0].permissions || [];
				const featuresList = permissionsAndFeatures[1].features_list || [];
				const nodes = featuresList.length
					? this.treeControl.dataNodes.filter((node) => featuresList.includes(node.name))
					: this.treeControl.dataNodes;
				this.accountPermissions = permissions;

				this.preselectEc2ReadonlyPermissions();

				nodes.forEach((node) => {
					const permissionNodes = this.treeControl
						.getDescendants(node)
						.filter((desc) => !(desc.children && desc.children.length));
					permissionNodes.forEach((perm) => {
						if (this.accountPermissions.hasOwnProperty(perm.name)) {
							this.leafSelectionToggle(perm);
						}
					});
				});

				this.checkPreselectedPermissions();
				this.disableMandatoryPermissions();
				this.treeLoadingWorkAround();
			});
	}

	/**
	 * During reonboarding only previously submitted permissions are preselected in the tree,
	 * so when we add new permissions to the "EC2 (Read-Only)" user can`t select them cuz it`s disabled for toggling.
	 * This method maps all permissions from "EC2 (Read-Only)" onto user`s previous permissions
	 */
	private preselectEc2ReadonlyPermissions() {
		const ec2Node = this.treeControl.dataNodes.filter((n) => n.name === 'EC2 (Read-Only)').shift();
		const ec2Permissions: PermissionNode[] = this.treeControl
			.getDescendants(ec2Node)
			.filter((desc) => !(desc.children && desc.children.length));
		const permissionsFormatted = {};
		ec2Permissions.forEach((k) => (permissionsFormatted[k.name] = 1));

		Object.assign(this.accountPermissions, permissionsFormatted);
	}

	private treeLoadingWorkAround(): void {
		// add normal animation time later for faster tree render
		timer(300).subscribe(() => (this.delayTime = 300));
		this.isLoading = false;
		this.permissionTemplates.controls.control.enable();
	}

	private disableMandatoryPermissions() {
		this.treeControl.dataNodes.forEach((node) => {
			const permissionNodes = this.treeControl
				.getDescendants(node)
				.filter((desc) => !(desc.children && desc.children.length));
			permissionNodes.forEach((perm) => {
				if (this.mandatoryPermissions.includes(perm.name)) {
					perm.permanent = true;
				}
			});
		});
	}

	private checkPreselectedPermissions() {
		if (this.permissionsToUpdate && this.permissionsToUpdate.length) {
			this.treeControl.dataNodes.forEach((node) => {
				const permissionNodes = this.treeControl
					.getDescendants(node)
					.filter((desc) => !(desc.children && desc.children.length));
				permissionNodes.forEach((perm) => {
					if (this.permissionsToUpdate.includes(perm.name)) {
						perm.permanent = true;
						let parent = this.getParentNode(perm);
						while (parent) {
							parent.permanent = true;
							this.treeControl.expand(parent);
							parent = this.getParentNode(parent);
						}
						this.leafSelectionToggle(perm);
					}
				});
			});
		}
	}

	highlightPermission(permission: string): boolean {
		return this.permissionsToUpdate && this.permissionsToUpdate.includes(permission);
	}

	private castDataToTreeObject(): void {
		if (this.permissionsData) {
			for (const aggregation of Object.keys(this.permissionsData)) {
				const servicesList = this.permissionsData[aggregation][this.cloud]['Services']; // forming tree structure
				this.TREE_DATA[aggregation] = servicesList;

				// filter tree to remove empty permission categories
				for (const key of Object.keys(servicesList)) {
					const permissionCategories = servicesList[key];
					// merge write_opt and write_mandatory
					permissionCategories['write'] = [];
					if (permissionCategories.hasOwnProperty('write_opt')) {
						permissionCategories['write'].push(...permissionCategories['write_opt']);
						delete permissionCategories['write_opt'];
					}
					if (permissionCategories.hasOwnProperty('write_mandatory')) {
						this.mandatoryPermissions.push(...permissionCategories['write_mandatory']);
						permissionCategories['write'].push(...permissionCategories['write_mandatory']);
						delete permissionCategories['write_mandatory'];
					}

					Object.keys(permissionCategories).forEach((cat) => {
						if (!permissionCategories[cat].length) {
							delete permissionCategories[cat];
						}
					});
				}
			}
		}
	}

	private findSelectedNodes(
		treeControl: NestedTreeControl<PermissionNode>,
		tree: SelectionModel<PermissionNode>,
		descendantsPartiallySelected,
	): PermissionNode[] {
		const selected: PermissionNode[] = [];

		function findSelectedNestedNodes(nodes: PermissionNode[]) {
			nodes.forEach((node) => {
				if (node.children) {
					findSelectedNestedNodes(node.children);
				}
				if (tree.isSelected(node) && !descendantsPartiallySelected(node)) {
					selected.push(node);
				}
			});
		}

		findSelectedNestedNodes(treeControl.dataNodes);
		return selected;
	}

	private handleTemplatesChange(): void {
		this.permissionTemplates.valueChanges.subscribe((v) => {
			switch (v.control) {
				case 'readonly':
					this.selectNodesByTemplate(this.treeControl.dataNodes, this.defaultTemplate, true);
					break;
				case 'managed':
					this.selectNodesByTemplate(this.treeControl.dataNodes, this.managedTemplate);
					break;
			}
		});
	}

	private selectNodesByTemplate(
		treeNodes: PermissionNode[],
		template: PermissionNode[],
		excludeWritePermissions = false,
	): void {
		treeNodes.forEach((node) => {
			if (template.includes(node)) {
				this.checklistSelection.select(node);
				let descendants = this.treeControl.getDescendants(node);
				if (excludeWritePermissions && this._nodesNamesAllowingPartialTree.includes(node.name)) {
					this.checklistSelection.deselect(node);
					descendants.forEach((descendant) => this.checklistSelection.deselect(descendant));
					descendants = descendants.filter(
						(descendant) => descendant.name !== 'write' && descendant.parent.name !== 'write',
					);
				}
				this.checklistSelection.select(...descendants);
			} else {
				this.checklistSelection.deselect(node);
				const descendants = this.treeControl.getDescendants(node);
				this.checklistSelection.deselect(...descendants);
			}
		});
	}

	private expandServiceLevel() {
		this.treeControl.dataNodes.forEach((node) => {
			const nodeLevel = this.getLevel(node);
			if (nodeLevel === 0) {
				const children = this.treeControl.getChildren(node);
				children.forEach((n) => this.treeControl.expand(n));
			}
		});
	}

	/**
	 * Build the structure tree. The `value` is the Json object, or a sub-tree of a Json object.
	 * The return value is the list of `PermissionNode`.
	 */
	buildPermissionsTree(obj: { [key: string]: any }, level: number, parent, isPermanent = false): PermissionNode[] {
		return Object.keys(obj).reduce<PermissionNode[]>((accumulator, key) => {
			const value = obj[key];
			const node = new PermissionNode();
			node.name = key;

			if (level === 0) {
				// check root level for "permanent" flag value
				Object.keys(this.permissionsData).forEach((permKey) => {
					if (permKey === node.name) {
						isPermanent = this.permissionsData[permKey].options.permanent;
						if (this.cloud === this.cloudTypes.Azure) {
							this.permissionsData[permKey].options.default = true;
						}

						if (this.permissionsData[permKey].options.default) {
							this.defaultTemplate.push(node);
						}
						if (this.permissionsData[permKey].options.default_managed) {
							this.managedTemplate.push(node);
						}
					}
				});
				parent = null;
			}
			node.permanent = isPermanent;
			node.parent = parent;

			if (value != null) {
				if (typeof value === 'object') {
					node.children = this.buildPermissionsTree(value, level + 1, node, isPermanent);
				} else {
					node.name = value;
				}
			}
			return accumulator.concat(node);
		}, []);
	}

	isExpandable = (node: PermissionNode) => !!(node.children && node.children.length);

	isDisabled = (node: PermissionNode) => node.permanent;

	getChildren = (node: PermissionNode): PermissionNode[] => node.children;

	hasChild = (_: number, nodeData: PermissionNode) => !!(nodeData.children && nodeData.children.length);

	isFirstLevel = (_: number, node: PermissionNode): boolean => this.getLevel(node) === 1;

	isPermCategoryLevel = (_: number, node: PermissionNode): boolean => this.getLevel(node) === 2;

	isAllChildrenDisabled = (node: PermissionNode): boolean => node.children.every((childNode) => childNode.permanent);

	getLevel(node: PermissionNode, data: PermissionNode[] = this.dataSource.data): number {
		const path = data.find((branch) => {
			return this.treeControl.getDescendants(branch).some((n) => n.name === node.name);
		});
		return path ? this.getLevel(node, path.children) + 1 : 0;
	}

	/** Whether all the descendants of the node are selected. */
	descendantsAllSelected(node: PermissionNode): boolean {
		const descendants = this.treeControl.getDescendants(node);
		const descAllSelected =
			descendants.length > 0 &&
			descendants.every((child) => {
				return this.checklistSelection.isSelected(child);
			});
		return descAllSelected;
	}

	/** Whether part of the descendants are selected */
	descendantsPartiallySelected(node: PermissionNode): boolean {
		const descendants = this.treeControl.getDescendants(node) as PermissionNode[];
		const result = descendants.some((child) => {
			return this.checklistSelection.isSelected(child);
		});
		return result && !this.descendantsAllSelected(node);
	}

	/** Toggle the permission node selection. Select/deselect all the descendants node */
	permissionSelectionToggle(node: PermissionNode): void {
		this.checklistSelection.toggle(node);
		const descendants = this.treeControl.getDescendants(node);
		this.checklistSelection.isSelected(node)
			? this.checklistSelection.select(...descendants)
			: this.checklistSelection.deselect(...descendants);
		// Force update for the parent
		descendants.forEach((child) => this.checklistSelection.isSelected(child));
		this.checkAllParentsSelection(node);
		this.checkTemplateSelected();
	}

	private checkTemplateSelected() {
		const selectedNodes = this.findSelectedNodes(
			this.treeControl,
			this.checklistSelection,
			this.descendantsPartiallySelected.bind(this),
		);
		const selectedNodesJSON = JSON.stringify(selectedNodes, ['name', 'children']);
		const selectedMatchDefaultTemplate =
			selectedNodesJSON === JSON.stringify(this._initiallySelectedReadOnlyNodes, ['name', 'children']);
		const selectedMatchManagedTemplate =
			selectedNodesJSON === JSON.stringify(this._initiallySelectedManagedNodes, ['name', 'children']);
		if (selectedMatchDefaultTemplate) {
			this.permissionTemplates.patchValue({ control: 'readonly' }, { emitEvent: false });
		} else if (selectedMatchManagedTemplate) {
			this.permissionTemplates.patchValue({ control: 'managed' }, { emitEvent: false });
		} else {
			this.permissionTemplates.patchValue({ control: 'custom' }, { emitEvent: false });
		}
	}

	leafSelectionToggle(node: PermissionNode): void {
		this.checklistSelection.toggle(node);
		this.checkAllParentsSelection(node);
		this.checkTemplateSelected();
	}

	/* Checks all the parents when a leaf node is selected/unselected */
	checkAllParentsSelection(node: PermissionNode): void {
		let parent: PermissionNode | null = this.getParentNode(node);
		while (parent) {
			this.checkRootNodeSelection(parent);
			parent = this.getParentNode(parent);
		}
	}

	/** Check root node checked state and change it accordingly */
	checkRootNodeSelection(node: PermissionNode): void {
		const nodeSelected = this.checklistSelection.isSelected(node);
		const descendants = this.treeControl.getDescendants(node);
		const descAllSelected =
			descendants.length > 0 && descendants.every((child) => this.checklistSelection.isSelected(child));
		if (nodeSelected && !descAllSelected) {
			this.checklistSelection.deselect(node);
		} else if (!nodeSelected && descAllSelected) {
			this.checklistSelection.select(node);
		}
	}

	/* Get the parent node of a node */
	getParentNode(node: PermissionNode): PermissionNode | null {
		return node.parent;
	}

	collectPermissions(): IPermissionsTreeOutput['permissionsAndFeatures'] {
		const permissions = {};
		const featuresList = [];
		const partiallySelectedNodes = this.treeControl.dataNodes.filter((node) => this.descendantsPartiallySelected(node));
		const fullySelectedNodes = this.checklistSelection.selected;

		[...fullySelectedNodes, ...partiallySelectedNodes].forEach((node) => {
			if (!node.parent) {
				// if root node
				featuresList.push(node.name);
				const services = node.children;
				services.forEach((service) => {
					if (!permissions.hasOwnProperty(service.name)) {
						permissions[service.name] = []; // form payload object
					}
					service.children.forEach((category) => {
						category.children.forEach((perm) => {
							if (this.checklistSelection.isSelected(perm)) {
								permissions[service.name].push(perm.name);
							}
						});
					});
				});
			}
		});
		return { permissions, featuresList };
	}
}
